import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { unixToInputValue } from '@/shared/lib/datetime';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import { getListRows } from '@/shared/lib/pagination';
import type { ListResponse } from '@/shared/ui/list/types';

/** Insurance companies of a country (legacy `getInsurance(token, 1)` — Russia). */
const DEFAULT_COUNTRY_ID = 1;
/** Length of the `YYYY-MM-DD` part of a datetime-local value. */
const DATE_LENGTH = 10;

export const fetchCompanies = () =>
  postForm<ListResponse>('/insurance/get-list/', { country_id: DEFAULT_COUNTRY_ID });

export const saveCompany = (v: { id?: number; name: string; countryId: string }) =>
  submitForm('/insurance/save/', {
    name: v.name,
    country_id: v.countryId,
    insurance_id: v.id,
  });

// ---- insurance request (cart) view / edit ----------------------------------

export interface InsuranceFile {
  id: number;
  file_url?: string;
  file_name?: string;
  name?: string;
  file_size?: number;
}

export interface InsuranceRequest extends Record<string, unknown> {
  id: number;
  fio?: string;
  phone?: string | number;
  email?: string;
  user_id?: number | string;
  date_birth?: number;
  experience?: number | null;
  licence_plate?: string;
  mark_id?: number | string | null;
  model_id?: number | string | null;
  car_year?: number | string | null;
  manager_commentary?: string;
  manager_id?: number | string;
  status_id?: number | string;
  insurance_id?: number | string | null;
  referrer?: string;
  user_files?: InsuranceFile[] | null;
  manager_files?: InsuranceFile[] | null;
}

export interface InsuranceRequestValues {
  fio: string;
  phone: string;
  email: string;
  dateBirth: string;
  experience: string;
  licencePlate: string;
  notRegistered: boolean;
  markId: string;
  modelId: string;
  carYear: string;
  managerCommentary: string;
  statusId: string;
  insuranceId: string;
}

export const requestToValues = (item: InsuranceRequest): InsuranceRequestValues => ({
  fio: item.fio ?? '',
  phone: String(item.phone ?? ''),
  email: item.email ?? '',
  dateBirth: unixToInputValue(item.date_birth).slice(0, DATE_LENGTH),
  experience: item.experience == null ? '' : String(item.experience),
  licencePlate: item.licence_plate ?? '',
  notRegistered: !item.licence_plate,
  markId: String(item.mark_id ?? ''),
  modelId: String(item.model_id ?? ''),
  carYear: String(item.car_year ?? ''),
  managerCommentary: item.manager_commentary ?? '',
  statusId: String(item.status_id ?? ''),
  insuranceId: String(item.insurance_id ?? ''),
});

export async function fetchInsuranceRequest(id: number) {
  const res = await postForm<{ data?: InsuranceRequest }>('/insurance/request/view/', { id });
  return res.data;
}

export async function fetchInsuranceCompanyOptions() {
  return normalizeOptions(getListRows(await fetchCompanies(), ['data']));
}

export const saveInsuranceRequest = (item: InsuranceRequest, v: InsuranceRequestValues) =>
  submitForm('/insurance/request/save/', {
    id: item.id,
    status_id: v.statusId,
    insurance_id: v.insuranceId,
    fio: v.fio,
    phone: v.phone.replace(/\D/g, ''),
    email: v.email,
    date_birth: dateInputToUnix(v.dateBirth),
    experience: v.experience,
    licence_plate: v.notRegistered ? '' : v.licencePlate,
    mark_id: v.markId,
    model_id: v.modelId,
    car_year: v.carYear,
    manager_commentary: v.managerCommentary,
    referrer: item.referrer,
  });

export const INSURANCE_FILE_MAX_KB = 30_000;

export const uploadInsuranceFile = async (objectId: number, file: File) => {
  const res = await submitForm<{ success?: number; data?: InsuranceFile }>('/insurance/request/upload-file/', {
    object_id: objectId,
    file,
  });
  return res.data;
};

export const deleteInsuranceFile = (id: number) => submitForm('/insurance/request/delete-file/', { id });
